import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Car, Mail, Calendar, Gauge, Heart, GitCompare, Ship } from 'lucide-react';
import { useVehicles } from '@/contexts/VehicleContext';
import { API, BACKEND_URL } from '@/config';
import { getShipCountry } from '@/data/shipCountries';

const getImageUrl = (imagePath) => {
  if (!imagePath) return imagePath;
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  const vehicleImageMatch = imagePath.match(/\/uploads\/vehicle_images\/([^/]+)\/([^/]+)/);
  if (vehicleImageMatch) {
    return `${API}/images/${vehicleImageMatch[1]}/${vehicleImageMatch[2]}`;
  }
  const additionalImageMatch = imagePath.match(/\/uploads\/additional_images\/([^/]+)\/([^/]+)/);
  if (additionalImageMatch) {
    return `${API}/additional-images/${additionalImageMatch[1]}/${additionalImageMatch[2]}`;
  }
  return `${BACKEND_URL}${imagePath}`;
};

const VehicleCard = ({ vehicle, showShipCountry = false, showEta = false }) => {
  const navigate = useNavigate();
  const { toggleFavorite, isFavorite, toggleCompare, isInCompare } = useVehicles();

  const shipCountry = showShipCountry && vehicle.ship_to_country
    ? getShipCountry(vehicle.ship_to_country)
    : null;

  const formatEtaDate = (eta) => {
    const date = new Date(eta);
    if (!date || isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatPrice = (priceUsd) => {
    if (priceUsd) {
      return `$${priceUsd.toLocaleString()}`;
    }
    return 'Contact for Price';
  };

  const mainImage = vehicle.images && vehicle.images.length > 0 ? getImageUrl(vehicle.images[0]) : null;
  const hasImage = vehicle.images && vehicle.images.length > 0 && vehicle.images[0] && vehicle.images[0] !== '';

  return (
    <Card className="hover:shadow-xl transition-all duration-300 cursor-pointer group overflow-hidden">
      {/* Image Section */}
      <div 
        className="relative h-48 bg-gray-100 overflow-hidden"
        onClick={() => navigate(`/vehicles/${vehicle.id}`)}
      >
        {/* Quick Action Buttons */}
        <div className="absolute top-2 left-2 z-10 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleFavorite(vehicle);
            }}
            className={`p-2 rounded-full backdrop-blur-sm transition-colors ${
              isFavorite(vehicle.id)
                ? 'bg-red-500 text-white'
                : 'bg-white/90 text-gray-700 hover:bg-red-500 hover:text-white'
            }`}
            title={isFavorite(vehicle.id) ? 'Remove from favorites' : 'Add to favorites'}
          >
            <Heart className={`h-4 w-4 ${isFavorite(vehicle.id) ? 'fill-current' : ''}`} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleCompare(vehicle);
            }}
            className={`p-2 rounded-full backdrop-blur-sm transition-colors ${
              isInCompare(vehicle.id)
                ? 'bg-red-600 text-white'
                : 'bg-white/90 text-gray-700 hover:bg-red-600 hover:text-white'
            }`}
            title={isInCompare(vehicle.id) ? 'Remove from comparison' : 'Add to comparison'}
          >
            <GitCompare className="h-4 w-4" />
          </button>
        </div>
        {hasImage ? (
          <img 
            src={mainImage} 
            alt={`${vehicle.year} ${vehicle.model}`}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            onError={(e) => {
              e.target.style.display = 'none';
              const placeholder = document.createElement('div');
              placeholder.className = 'flex items-center justify-center h-full';
              placeholder.innerHTML = '<svg class="h-20 w-20 text-white opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"></path></svg>';
              e.target.parentElement.appendChild(placeholder);
            }}
          />
        ) : (
          <div className="flex flex-col items-center justify-center h-full">
            <Car className="h-20 w-20 text-white opacity-50" />
            <Badge className="mt-2 bg-yellow-500 text-white text-xs">
              Image Coming Soon
            </Badge>
          </div>
        )}
        {vehicle.status === 'available' && (
          <Badge className="absolute top-2 right-2 bg-green-600 text-white">
            Available
          </Badge>
        )}
        {vehicle.status === 'on_the_way' && (
          <Badge className="absolute top-2 right-2 bg-cyan-600 text-white">
            On the Way
          </Badge>
        )}
      </div>

      {/* Content Section */}
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex-1" onClick={() => navigate(`/vehicles/${vehicle.id}`)}>
            <CardTitle className="text-lg mb-1 group-hover:text-red-600 transition-colors">
              {vehicle.year} {vehicle.brand || ''} {vehicle.model}
            </CardTitle>
            <div className="flex items-center gap-2">
              {vehicle.stock_no && (
                <p className="text-xs text-gray-500">Stock No: {vehicle.stock_no}</p>
              )}
              {vehicle.grade && (
                <p className="text-xs text-gray-500">
                  {vehicle.stock_no ? '·' : ''} Grade: {vehicle.grade}
                </p>
              )}
            </div>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {shipCountry && (
          <div className="flex items-center gap-1.5 text-xs text-cyan-700 bg-cyan-50 border border-cyan-200 rounded-md px-2 py-1.5">
            <Ship className="h-3 w-3 flex-shrink-0" />
            <span>
              {shipCountry.emoji} On the way to {shipCountry.name}
            </span>
          </div>
        )}

        {showEta && vehicle.eta && (
          <div className="flex items-center gap-1.5 text-xs text-slate-600">
            <Calendar className="h-3 w-3 flex-shrink-0" />
            <span>ETA: {formatEtaDate(vehicle.eta)}</span>
          </div>
        )}

        {/* Quick Info */}
        <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
          <div className="flex items-center space-x-1">
            <Calendar className="h-3 w-3" />
            <span>{vehicle.year}</span>
          </div>
          {vehicle.mileage && (
            <div className="flex items-center space-x-1">
              <Gauge className="h-3 w-3" />
              <span>{vehicle.mileage.toLocaleString()} km</span>
            </div>
          )}
          {vehicle.fuel_type && (
            <div className="text-xs">
              <Badge variant="outline" className="text-xs">{vehicle.fuel_type}</Badge>
            </div>
          )}
          {vehicle.transmission && (
            <div className="text-xs">
              <Badge variant="outline" className="text-xs">{vehicle.transmission}</Badge>
            </div>
          )}
        </div>

        {/* Price & Action */}
        <div className="border-t pt-4">
          <div className="mb-3">
            <p className="text-2xl font-bold text-red-600">
              {formatPrice(vehicle.public_price_usd)}
            </p>
          </div>
          <Button 
            className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 shadow-md"
            onClick={() => navigate(`/vehicles/${vehicle.id}`)}
          >
            View Details
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default VehicleCard;